import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isMentionReviewReady } from './EpisodeMentionReviewDialog';
import {
    Box,
    Typography,
    Avatar,
    Chip,
    IconButton,
    Button,
    Tooltip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Popover,
    Divider
} from '@mui/material';
import {
    Business as BusinessIcon,
    PlayArrow as PlayIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    FileCopy as DuplicateIcon,
    ThumbDownAlt as ThumbDownIcon,
    Label as LabelIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { BrandMention } from '../services/api';
import apiService from '../services/api';
import { getBrandLogoProps } from '../utils/brandLogoUtils';
import { DEFAULT_REJECT_REASON, REJECT_MENTION_REASON_PRESETS } from '../utils/rejectMentionReasonPresets';
import {
    VISUAL_CUE_OPTIONS,
    VISUAL_CUE_CHIP_COLORS,
    visualCueLabel,
    type VisualCue,
} from '../utils/visualCueLabels';

type PlacementType = 'content' | 'opening_credits' | 'closing_credits' | 'mid_credits' | 'physical_placement' | 'banner' | 'overlay';

interface BrandMentionRowProps {
    mention: BrandMention;
    contentUrl?: string;
    showMentionTranscript?: boolean;
    onDelete: (mentionId: string) => void;
    onEdit: (mention: BrandMention) => void;
    onDuplicate: (mention: BrandMention) => void;
    onCopyMentionId: (mentionId: string) => void;
    onFeedback?: (
        mentionId: string,
        status: 'rejected' | null,
        reason?: string,
        context?: string,
    ) => void;
    onClassificationChange?: (mentionId: string, mentionType?: string, placementType?: string, visualCue?: VisualCue | null) => void;
    formatTime: (seconds: number) => string;
    getClassificationColor: (classification: string) => string;
    createYouTubeUrlWithTimestamp: (url: string, startTime: number) => string;
    isDeleting?: boolean;
}

const MENTION_TYPES = [
    { value: 'paid', label: 'Paid', color: '#f44336' },
    { value: 'organic', label: 'Organic', color: '#4caf50' },
    { value: 'neutral', label: 'Neutral', color: '#9e9e9e' },
];

const PLACEMENT_TYPES: { value: PlacementType; label: string; color: string }[] = [
    { value: 'content', label: 'Content', color: '#1976d2' },
    { value: 'opening_credits', label: 'Opening credits', color: '#7b1fa2' },
    { value: 'mid_credits', label: 'Mid credits', color: '#8e24aa' },
    { value: 'closing_credits', label: 'Closing credits', color: '#7b1fa2' },
    { value: 'physical_placement', label: 'Physical', color: '#e65100' },
    { value: 'overlay', label: 'Overlay', color: '#f9a825' },
    { value: 'banner', label: 'Banner', color: '#2e7d32' },
];

const BrandMentionRow: React.FC<BrandMentionRowProps> = ({
    mention,
    contentUrl,
    showMentionTranscript = false,
    onDelete,
    onEdit,
    onDuplicate,
    onCopyMentionId,
    onFeedback,
    onClassificationChange,
    formatTime,
    getClassificationColor,
    createYouTubeUrlWithTimestamp,
    isDeleting = false
}) => {
    const [localDeleting, setLocalDeleting] = useState(false);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackReason, setFeedbackReason] = useState('');
    const [feedbackContext, setFeedbackContext] = useState('');
    const [localFeedback, setLocalFeedback] = useState<BrandMention['feedback']>(mention.feedback);
    const [submittingFeedback, setSubmittingFeedback] = useState(false);

    // Categorization menu state
    const [categoryAnchorEl, setCategoryAnchorEl] = useState<HTMLElement | null>(null);
    const categoryMenuOpen = Boolean(categoryAnchorEl);

    const handleCategoryMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCategoryAnchorEl(event.currentTarget);
    };

    const handleCategoryMenuClose = () => {
        setCategoryAnchorEl(null);
    };
    const [localClassification, setLocalClassification] = useState(mention.classification);
    const [localPlacementType, setLocalPlacementType] = useState<PlacementType | undefined>(
        mention.placementType as PlacementType | undefined,
    );
    const [localVisualCue, setLocalVisualCue] = useState<VisualCue | null>(mention.visualCue ?? null);
    const [savingClassification, setSavingClassification] = useState(false);

    useEffect(() => {
        setLocalClassification(mention.classification);
        setLocalPlacementType(mention.placementType as PlacementType | undefined);
        setLocalVisualCue(mention.visualCue ?? null);
    }, [
        mention._id,
        mention.classification,
        mention.placementType,
        mention.visualCue,
    ]);

    useEffect(() => {
        setLocalFeedback(mention.feedback);
    }, [mention._id, mention.feedback?.status, mention.feedback?.reason, mention.feedback?.context]);

    const [visualCueAnchorEl, setVisualCueAnchorEl] = useState<HTMLElement | null>(null);
    const visualCueMenuOpen = Boolean(visualCueAnchorEl);

    const { data: brandData } = useQuery({
        queryKey: ['brand', mention.brandId],
        queryFn: async () => {
            const data = await apiService.getBrands(1, 1000);
            // React Query forbids undefined — return null when not in the page.
            return data.brands.find((brand) => brand._id === mention.brandId) ?? null;
        },
        enabled: !!mention.brandId,
        staleTime: 5 * 60 * 1000,
    });

    const displayBrandName = brandData?.name || mention.brandName;
    const displayLogoUrl = getBrandLogoProps(mention).src;

    const isRejected = localFeedback?.status === 'rejected';

    const handleDelete = async (event?: React.MouseEvent) => {
        const skipConfirm = event?.metaKey || event?.ctrlKey;
        if (skipConfirm || window.confirm('Are you sure you want to delete this brand mention? This action cannot be undone.')) {
            setLocalDeleting(true);
            try {
                await onDelete(mention._id);
            } catch (error) {
                console.error('Failed to delete brand mention:', error);
            } finally {
                setLocalDeleting(false);
            }
        }
    };

    const handleFeedbackConfirm = async () => {
        const reason = feedbackReason.trim();
        if (!reason) return;
        const context = feedbackContext.trim() || undefined;
        setSubmittingFeedback(true);
        try {
            await apiService.updateMentionFeedback(mention._id, 'rejected', reason, context);
            const newFeedback = {
                status: 'rejected' as const,
                reason,
                context,
            };
            setLocalFeedback(newFeedback);
            onFeedback?.(mention._id, 'rejected', reason, context);
            setFeedbackDialogOpen(false);
            setFeedbackReason('');
            setFeedbackContext('');
        } catch (error) {
            console.error('Failed to update mention feedback:', error);
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const handleFeedbackUndo = async () => {
        setSubmittingFeedback(true);
        try {
            await apiService.updateMentionFeedback(mention._id, null);
            setLocalFeedback(null);
            onFeedback?.(mention._id, null);
        } catch (error) {
            console.error('Failed to undo mention feedback:', error);
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const handleClassificationClick = async (mentionType: string) => {
        setSavingClassification(true);
        try {
            await apiService.updateMentionClassification(
                { ...mention, classification: localClassification, placementType: localPlacementType, visualCue: localVisualCue },
                { mentionType }
            );
            setLocalClassification(mentionType);
            onClassificationChange?.(mention._id, mentionType, localPlacementType, localVisualCue);
        } catch (error) {
            console.error('Failed to update mention type:', error);
        } finally {
            setSavingClassification(false);
        }
    };

    const handlePlacementClick = async (placementType: PlacementType) => {
        setSavingClassification(true);
        try {
            await apiService.updateMentionClassification(
                { ...mention, classification: localClassification, placementType: localPlacementType, visualCue: localVisualCue },
                { placementType }
            );
            setLocalPlacementType(placementType);
            onClassificationChange?.(mention._id, localClassification, placementType, localVisualCue);
        } catch (error) {
            console.error('Failed to update placement type:', error);
        } finally {
            setSavingClassification(false);
        }
    };

    const handleVisualCueClick = async (cue: VisualCue | null) => {
        setSavingClassification(true);
        try {
            await apiService.updateMentionClassification(
                { ...mention, classification: localClassification, placementType: localPlacementType, visualCue: localVisualCue },
                { visualCue: cue }
            );
            setLocalVisualCue(cue);
            onClassificationChange?.(mention._id, localClassification, localPlacementType, cue);
            setVisualCueAnchorEl(null);
        } catch (error) {
            console.error('Failed to update visual cue:', error);
        } finally {
            setSavingClassification(false);
        }
    };

    const isRowDeleting = isDeleting || localDeleting;

    const activePlacement = PLACEMENT_TYPES.find(p => p.value === localPlacementType);

    return (
        <>
            <Box
                sx={{
                    py: 1.5,
                    px: 0,
                    opacity: isRowDeleting ? 0.6 : isRejected ? 0.45 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                }}
            >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    {displayLogoUrl ? (
                        <Avatar
                            src={displayLogoUrl}
                            sx={{ width: 20, height: 20 }}
                        />
                    ) : (
                        <BusinessIcon color="primary" sx={{ fontSize: 20 }} />
                    )}
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={isRejected ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                    >
                        {displayBrandName}
                    </Typography>
                    <Chip
                        label={localClassification}
                        size="small"
                        sx={{
                            bgcolor: getClassificationColor(localClassification),
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px'
                        }}
                    />
                    {activePlacement && (
                        <Chip
                            label={activePlacement.label}
                            size="small"
                            sx={{
                                bgcolor: activePlacement.color,
                                color: 'white',
                                fontSize: '0.7rem',
                                height: '20px'
                            }}
                        />
                    )}
                    <Tooltip title="Set visual hint (banner, curtain, etc.)">
                        <Chip
                            label={localVisualCue ? visualCueLabel(localVisualCue) : 'Visual hint'}
                            size="small"
                            color={localVisualCue ? (VISUAL_CUE_CHIP_COLORS[localVisualCue] ?? 'default') : 'default'}
                            variant={localVisualCue ? 'filled' : 'outlined'}
                            onClick={(e) => {
                                if (!isRowDeleting && !isRejected) {
                                    setVisualCueAnchorEl(e.currentTarget);
                                }
                            }}
                            sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                cursor: isRowDeleting || isRejected ? 'default' : 'pointer',
                            }}
                        />
                    </Tooltip>
                    {isMentionReviewReady(mention) && (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="Ready"
                            size="small"
                            color="success"
                            sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                            }}
                        />
                    )}
                    {isRejected && (
                        <Chip
                            label="Not a mention"
                            size="small"
                            sx={{
                                bgcolor: 'warning.main',
                                color: 'white',
                                fontSize: '0.7rem',
                                height: '20px'
                            }}
                        />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                    {mention.context}
                </Typography>
                {showMentionTranscript && mention.transcriptExcerpt && mention.transcriptExcerpt.length > 0 && (
                    <Box
                        component="pre"
                        sx={{
                            mt: 0.75,
                            mb: 0,
                            p: 1,
                            fontSize: '0.7rem',
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                        }}
                    >
                        {mention.transcriptExcerpt.map((seg) =>
                            `[${formatTime(seg.start)}–${formatTime(seg.end)}] ${seg.text}`,
                        ).join('\n\n')}
                    </Box>
                )}
                {isRejected && localFeedback?.reason && (
                    <Typography variant="caption" color="warning.main" display="block">
                        Reason: "{localFeedback.reason}"
                    </Typography>
                )}
                {isRejected && localFeedback?.context && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Context: {localFeedback.context}
                    </Typography>
                )}
                <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                        {formatTime(mention.startTime)} - {formatTime(mention.endTime)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        • {mention.commercialConfidence}% commercial confidence
                    </Typography>
                    <Tooltip title="Copy mention ID">
                        <IconButton
                            size="small"
                            onClick={() => onCopyMentionId(mention._id)}
                            sx={{ p: 0.5 }}
                            disabled={isRowDeleting}
                        >
                            <CopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate mention">
                        <IconButton
                            size="small"
                            onClick={() => onDuplicate(mention)}
                            sx={{ p: 0.5 }}
                            disabled={isRowDeleting}
                        >
                            <DuplicateIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {contentUrl && (
                        <Button
                            size="small"
                            startIcon={<PlayIcon />}
                            onClick={() => {
                                window.open(
                                    createYouTubeUrlWithTimestamp(contentUrl, mention.segmentStart ?? mention.startTime),
                                    '_blank'
                                )
                            }}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                            disabled={isRowDeleting}
                        >
                            Play
                        </Button>
                    )}
                </Box>
                <Box
                    display="flex"
                    gap={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    mt={1}
                >
                    <Tooltip title="Categorize mention type and placement">
                        <IconButton
                            size="small"
                            onClick={handleCategoryMenuOpen}
                            disabled={isRowDeleting}
                            sx={{ color: (categoryMenuOpen || localPlacementType || localClassification !== mention.classification) ? 'primary.main' : 'text.secondary' }}
                        >
                            {savingClassification ? <CircularProgress size={16} /> : <LabelIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isRejected ? 'Undo — restore as mention' : 'Mark as not a mention'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={isRejected ? handleFeedbackUndo : () => {
                                    setFeedbackReason(DEFAULT_REJECT_REASON);
                                    setFeedbackContext('');
                                    setFeedbackDialogOpen(true);
                                }}
                                disabled={isRowDeleting || submittingFeedback}
                                sx={{ color: isRejected ? 'warning.main' : 'text.secondary' }}
                            >
                                {submittingFeedback ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <ThumbDownIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                        onClick={() => onEdit(mention)}
                        disabled={isRowDeleting}
                        sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem' }}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={isRowDeleting ? undefined : <DeleteIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => handleDelete(e)}
                        disabled={isRowDeleting || !mention.review?.humanAdded}
                        sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', display: mention.review?.humanAdded ? undefined : 'none' }}
                    >
                        {isRowDeleting ? <CircularProgress size={14} /> : 'Delete'}
                    </Button>
                </Box>
            </Box>

            {/* Categorization popover */}
            <Popover
                open={categoryMenuOpen}
                anchorEl={categoryAnchorEl}
                onClose={handleCategoryMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableScrollLock
                slotProps={{
                    paper: {
                        sx: { minWidth: 260, p: 1.5, mt: 0.5 },
                    },
                }}
            >
                <Box onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                        MENTION TYPE
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
                        {MENTION_TYPES.map((t) => (
                            <Chip
                                key={t.value}
                                label={t.label}
                                size="small"
                                onClick={() => handleClassificationClick(t.value)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: localClassification === t.value ? t.color : 'transparent',
                                    color: localClassification === t.value ? 'white' : t.color,
                                    borderColor: t.color,
                                    border: '1px solid',
                                    fontWeight: localClassification === t.value ? 'bold' : 'normal',
                                }}
                            />
                        ))}
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                        PLACEMENT
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {PLACEMENT_TYPES.map((p) => (
                            <Chip
                                key={p.value}
                                label={p.label}
                                size="small"
                                onClick={() => handlePlacementClick(p.value)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: localPlacementType === p.value ? p.color : 'transparent',
                                    color: localPlacementType === p.value ? 'white' : p.color,
                                    borderColor: p.color,
                                    border: '1px solid',
                                    fontWeight: localPlacementType === p.value ? 'bold' : 'normal',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Popover>

            <Popover
                open={visualCueMenuOpen}
                anchorEl={visualCueAnchorEl}
                onClose={() => setVisualCueAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableScrollLock
                slotProps={{
                    paper: {
                        sx: { minWidth: 220, p: 1.5, mt: 0.5 },
                    },
                }}
            >
                <Box onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                        VISUAL HINT
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {VISUAL_CUE_OPTIONS.map((opt) => {
                            const cueValue = opt.value || null;
                            const isActive = (localVisualCue ?? null) === cueValue;
                            return (
                                <Chip
                                    key={opt.label}
                                    label={opt.label}
                                    size="small"
                                    onClick={() => handleVisualCueClick(cueValue)}
                                    disabled={savingClassification}
                                    color={cueValue ? (VISUAL_CUE_CHIP_COLORS[cueValue] ?? 'default') : 'default'}
                                    variant={isActive ? 'filled' : 'outlined'}
                                    sx={{ cursor: 'pointer' }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            </Popover>

            {/* Not-a-mention feedback dialog */}
            <Dialog
                open={feedbackDialogOpen}
                onClose={() => {
                    setFeedbackDialogOpen(false);
                    setFeedbackReason('');
                    setFeedbackContext('');
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Mark as not a mention</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 1 }}>
                        Pick a rejection reason. Add context only if you need extra detail.
                    </Typography>
                    <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                        {REJECT_MENTION_REASON_PRESETS.map((preset) => (
                            <Chip
                                key={preset}
                                label={preset}
                                size="small"
                                onClick={() => setFeedbackReason(preset)}
                                color={feedbackReason === preset ? 'warning' : 'default'}
                                variant={feedbackReason === preset ? 'filled' : 'outlined'}
                                sx={{ cursor: 'pointer' }}
                            />
                        ))}
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Context (optional)"
                        value={feedbackContext}
                        onChange={(e) => setFeedbackContext(e.target.value)}
                        placeholder='e.g. "mundo in todo el mundo" or duped of <mentionId>'
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setFeedbackDialogOpen(false);
                            setFeedbackReason('');
                            setFeedbackContext('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFeedbackConfirm}
                        variant="contained"
                        color="warning"
                        disabled={submittingFeedback || !feedbackReason.trim()}
                    >
                        {submittingFeedback ? <CircularProgress size={16} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BrandMentionRow;
